import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { 
  CERTIFICATE_REQUEST_CREATE, 
  CERTIFICATE_REQUEST_UPDATE,
  INSURANCE_POLICIES_LIST 
} from '../../graphql/insurancePolicies';
import { CUSTOMER_LIST } from '../../graphql/customers';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { 
  Email as EmailIcon, 
  Warning as WarningIcon, 
  CheckCircle as CheckCircleIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import DateRangePicker from '../../components/date-range-picker/DateRangePicker';
import axios from 'axios';
import { useAuthContext } from '../../sections/auth';
import EmailService from '../../services/emailService';
import { EMAIL_TEMPLATE_GET_BY_USE_CASE } from '../../graphql/emailTemplates';
import { handleCertificateRequestWorkflow } from '../../utils/emailWorkflow';
import { EMAIL_LOG_CREATE } from '../../graphql/emailLogs';

const CertificateRequestForm = ({ open, onClose, request = null, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    clientId: '',
    insurancePolicyId: '',
    requestReason: '',
    status: 'pending',
    certificateNumber: '',
    effectiveDates: '',
    effectiveStartDate: null,
    effectiveEndDate: null,
    certificateDocument: null,
    requestDate: new Date(),
    emailSentDate: null,
    certificateReceivedDate: null
  });

  // Email template data
  const [emailData, setEmailData] = useState({
    subject: '',
    body: '',
    recipients: [],
    attachments: []
  });

  // Email connection status
  const [emailConnected, setEmailConnected] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailType, setEmailType] = useState('gmail'); // or 'outlook'

  // Date range picker state
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(false);

  // Auth context
  const { user } = useAuthContext();
  
  // Queries
  const { data: customersData, loading: customersLoading } = useQuery(CUSTOMER_LIST);
  const { data: policiesData, loading: policiesLoading } = useQuery(INSURANCE_POLICIES_LIST);
  const { data: emailTemplatesData, loading: emailTemplatesLoading } = useQuery(
    EMAIL_TEMPLATE_GET_BY_USE_CASE,
    { variables: { category: 'certificate_request' } }
  );

  // Mutations
  const [createRequest, { loading: createLoading }] = useMutation(CERTIFICATE_REQUEST_CREATE);
  const [updateRequest, { loading: updateLoading }] = useMutation(CERTIFICATE_REQUEST_UPDATE);
  const [createEmailLog] = useMutation(EMAIL_LOG_CREATE);

  useEffect(() => {
    if (request) {
      // Parse effective dates if they exist
      let effectiveStartDate = null;
      let effectiveEndDate = null;
      if (request.effectiveDates) {
        // Handle different types of effectiveDates
        let dateString = '';
        if (typeof request.effectiveDates === 'string') {
          dateString = request.effectiveDates;
        } else if (request.effectiveDates instanceof Date) {
          dateString = request.effectiveDates.toISOString().split('T')[0];
        } else {
          // If it's an object or other type, try to convert to string
          dateString = String(request.effectiveDates);
        }
        
        const dateParts = dateString.split(' to ');
        if (dateParts.length === 2) {
          effectiveStartDate = new Date(dateParts[0]);
          effectiveEndDate = new Date(dateParts[1]);
        } else {
          // If it's a single date, use it as start date
          effectiveStartDate = new Date(dateString);
        }
      }

      setFormData({
        clientId: request.client?.id || '',
        insurancePolicyId: request.insurancePolicy?.id || '',
        requestReason: request.requestReason || '',
        status: request.status || 'pending',
        certificateNumber: request.certificateNumber || '',
        effectiveDates: request.effectiveDates || '',
        effectiveStartDate,
        effectiveEndDate,
        certificateDocument: request.certificateDocument || null,
        requestDate: request.requestDate ? new Date(request.requestDate) : new Date(),
        emailSentDate: request.emailSentDate ? new Date(request.emailSentDate) : null,
        certificateReceivedDate: request.certificateReceivedDate ? new Date(request.certificateReceivedDate) : null
      });
      setEmailSent(!!request.emailSentDate);
    } else {
      setFormData({
        clientId: '',
        insurancePolicyId: '',
        requestReason: '',
        status: 'pending',
        certificateNumber: '',
        effectiveDates: '',
        effectiveStartDate: null,
        effectiveEndDate: null,
        certificateDocument: null,
        requestDate: new Date(),
        emailSentDate: null,
        certificateReceivedDate: null
      });
      setEmailSent(false);
    }
    setActiveStep(0);
  }, [request]);

  // Check email connection status
  useEffect(() => {
    const googleToken = localStorage.getItem("google_access_token");
    const outlookToken = localStorage.getItem("outlook_access_token");
    
    if (googleToken) {
      setEmailConnected(true);
      setEmailType('gmail');
    } else if (outlookToken) {
      setEmailConnected(true);
      setEmailType('outlook');
    } else {
      setEmailConnected(false);
    }
  }, []);

  const steps = ['Select Client & Policy', 'Review & Send Email', 'Upload Certificate & Complete'];

  // Date range picker handlers
  const handleDateRangeChange = (startDate, endDate) => {
    setFormData(prev => ({
      ...prev,
      effectiveStartDate: startDate,
      effectiveEndDate: endDate,
      effectiveDates: startDate && endDate ? 
        `${dayjs(startDate).format('MM/DD/YYYY')} to ${dayjs(endDate).format('MM/DD/YYYY')}` : 
        ''
    }));
  };

  const handleDateRangeSubmit = () => {
    if (formData.effectiveStartDate && formData.effectiveEndDate) {
      if (formData.effectiveEndDate < formData.effectiveStartDate) {
        setDateRangeError(true);
        return;
      }
      setDateRangeError(false);
    }
    setDateRangePickerOpen(false);
  };

  const handleDateRangeClear = () => {
    setFormData(prev => ({
      ...prev,
      effectiveStartDate: null,
      effectiveEndDate: null,
      effectiveDates: ''
    }));
    setDateRangeError(false);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate step 1
      if (!formData.clientId || !formData.insurancePolicyId) {
        enqueueSnackbar('Please select both client and insurance policy', { variant: 'error' });
        return;
      }
      // Load email template
      loadEmailTemplate();
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const loadEmailTemplate = () => {
    const selectedClient = customersData?.customersList?.items?.find(client => client.id === formData.clientId);
    const selectedPolicy = policiesData?.insurancePoliciesList?.items?.find(policy => policy.id === formData.insurancePolicyId);

    if (selectedClient && selectedPolicy) {
      // Try to get template from 8base
      const template = emailTemplatesData?.emailTemplatesList?.items?.find(t => t.isActive) || null;
      
      if (template) {
        // Process template with variables
        const processedTemplate = processTemplate(template, selectedClient, selectedPolicy);
        
        setEmailData({
          subject: processedTemplate.subject,
          body: processedTemplate.body,
          recipients: [selectedPolicy.agentEmail || 'xopes49335@noidem.com'],
          attachments: []
        });
      } else {
        console.log('📧 No 8base template found, using fallback');
        
        // Fallback template
        const fallbackTemplate = {
          subject: `Additional Insured Certificate Request - ${selectedClient.name}`,
          body: `Dear Insurance Agent,

I am requesting an Additional Insured Certificate for the following client:

CLIENT INFORMATION:
- Company Name: ${selectedClient.name}
- Address: ${selectedClient.address1 || 'N/A'}
- City, State, ZIP: ${selectedClient.city || 'N/A'}, ${selectedClient.state || 'N/A'} ${selectedClient.zip || 'N/A'}
- Contact Person: ${selectedClient.contactPerson || 'N/A'}
- Phone: ${selectedClient.phone || 'N/A'}
- Email: ${selectedClient.email || 'N/A'}

POLICY INFORMATION:
- Policy Number: ${selectedPolicy.policyNumber}
- Insurance Provider: ${selectedPolicy.provider}
- Coverage Type: ${selectedPolicy.coverageType || 'N/A'}
- Business Unit: ${selectedPolicy.businessUnits?.name || 'N/A'}
- Agent Name: ${selectedPolicy.agentName || 'N/A'}
- Agent Email: ${selectedPolicy.agentEmail || 'N/A'}

REQUEST DETAILS:
- Request Reason: ${formData.requestReason || 'Standard additional insured certificate request'}
- Request Date: ${dayjs().format('MMMM DD, YYYY')}
- Requested By: ${user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'Fleet Manager'}

CERTIFICATE REQUIREMENTS:
Please provide the Additional Insured Certificate for this client. The certificate should include:
- Client name as additional insured
- Policy number and effective dates
- Coverage limits and types
- Any specific requirements or endorsements
- Certificate holder information

Please send the completed certificate to our email address for processing.

Thank you for your prompt attention to this matter.

Best regards,
${user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'Fleet Manager'}
Fleet Management Team`,
          recipients: [selectedPolicy.agentEmail || 'xopes49335@noidem.com'],
          attachments: []
        };

        setEmailData(fallbackTemplate);
      }
    }
  };

  // Process template with dynamic variables
  const processTemplate = (template, selectedClient, selectedPolicy) => {
    if (!template) return { subject: '', body: '' };
    
    const currentDate = dayjs().format('YYYY-MM-DD');
    
    let processedSubject = template.subject;
    let processedBody = template.body;
    
    // Replace all variables with more flexible data access
    const replacements = [
      { pattern: /\{\{clientFullName\}\}/g, value: selectedClient?.name || 'N/A' },
      { pattern: /\{\{clientName\}\}/g, value: selectedClient?.name || 'N/A' },
      { pattern: /\{\{clientEmail\}\}/g, value: selectedClient?.email || 'N/A' },
      { pattern: /\{\{clientAddress\}\}/g, value: selectedClient?.address1 || 'N/A' },
      { pattern: /\{\{clientPhone\}\}/g, value: selectedClient?.phone || 'N/A' },
      { pattern: /\{\{policyNumber\}\}/g, value: selectedPolicy?.policyNumber || selectedPolicy?.policy_number || 'N/A' },
      { pattern: /\{\{policyType\}\}/g, value: selectedPolicy?.coverageType || selectedPolicy?.coverage_type || 'N/A' },
      { pattern: /\{\{insuranceCompany\}\}/g, value: selectedPolicy?.provider || 'N/A' },
      { pattern: /\{\{agentName\}\}/g, value: selectedPolicy?.agentName || selectedPolicy?.agent_name || 'Insurance Agent' },
      { pattern: /\{\{agentEmail\}\}/g, value: selectedPolicy?.agentEmail || selectedPolicy?.agent_email || 'xopes49335@noidem.com' },
      { pattern: /\{\{businessUnitName\}\}/g, value: selectedPolicy?.businessUnits?.name || selectedPolicy?.businessUnit?.name || 'N/A' },
      { pattern: /\{\{requestedByName\}\}/g, value: user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'User' },
      { pattern: /\{\{currentDate\}\}/g, value: currentDate },
      { pattern: /\{\{requestDate\}\}/g, value: currentDate },
      { pattern: /\{\{requestReason\}\}/g, value: formData.requestReason || 'Certificate request' },
      { pattern: /\{\{certificateNumber\}\}/g, value: formData.certificateNumber || 'Pending' },
      { pattern: /\{\{effectiveDates\}\}/g, value: formData.effectiveDates || 'To be determined' }
    ];
    
    replacements.forEach(({ pattern, value }) => {
      processedSubject = processedSubject.replace(pattern, value);
      processedBody = processedBody.replace(pattern, value);
    });
    
    
    return {
      subject: processedSubject,
      body: processedBody
    };
  };

  const handleSendEmail = async () => {
    if (!emailConnected) {
      enqueueSnackbar('Please connect your email account first from settings', { variant: 'error' });
      return;
    }

    setEmailLoading(true);
    
    try {
      const formData = {
        to: emailData.recipients.join(', '),
        subject: emailData.subject,
        message: emailData.body
      };

      if (emailType === 'gmail') {
        const token = localStorage.getItem("google_access_token");
        await sendGmailEmail(formData, token);
      } else if (emailType === 'outlook') {
        const token = localStorage.getItem("outlook_access_token");
        await sendOutlookEmail(formData, token);
      }
      
      setEmailSent(true);
      // Auto-fill emailSentDate when email is sent
      setFormData(prev => ({
        ...prev,
        emailSentDate: new Date()
      }));
      enqueueSnackbar(`Email sent successfully via ${emailType.toUpperCase()}`, { variant: 'success' });
      handleNext();
    } catch (error) {
      enqueueSnackbar(`Error sending email: ${error.message}`, { variant: 'error' });
    } finally {
      setEmailLoading(false);
    }
  };

  const sendGmailEmail = async (formData, token) => {
    const message = [
      "To: " + formData.to,
      "Subject: " + formData.subject,
      "",
      formData.message,
    ].join("\n");

    try {
      await axios.post(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/send`,
        {
          raw: btoa(message),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      if (localStorage.getItem("google_refresh_token")) {
        // Try to refresh token
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_GOOGLE_URL}/token`,
          {
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
            refresh_token: localStorage.getItem("google_refresh_token"),
            grant_type: "refresh_token",
          },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Host: "oauth2.googleapis.com",
            },
          }
        );
        
        if (refreshResponse.data) {
          localStorage.setItem("google_access_token", refreshResponse.data.access_token);
          // Retry with new token
          await sendGmailEmail(formData, refreshResponse.data.access_token);
        }
      } else {
        throw new Error("Please configure your Gmail account from settings!");
      }
    }
  };

  const sendOutlookEmail = async (formData, token) => {
    const payload = {
      message: {
        subject: formData.subject,
        body: {
          contentType: "Text",
          content: formData.message,
        },
        toRecipients: [
          {
            emailAddress: {
              address: formData.to,
            },
          },
        ],
      },
    };

    try {
      await axios.post(
        "https://graph.microsoft.com/v1.0/me/sendMail",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      throw new Error("Please configure your Outlook account from settings!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format dates properly for 8base (YYYY-MM-DD format)
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const variables = {
        data: {
          client: { connect: { id: formData.clientId } },
          insurancePolicy: { connect: { id: formData.insurancePolicyId } },
          status: formData.status,
          certificateNumber: formData.certificateNumber,
          effectiveDates: formData.effectiveStartDate ? formData.effectiveStartDate.toISOString().split('T')[0] : null,
          requestDate: formattedDate,
          emailSentDate: formData.emailSentDate ? formData.emailSentDate.toISOString().split('T')[0] : null,
          certificateReceivedDate: formData.certificateReceivedDate ? formData.certificateReceivedDate.toISOString().split('T')[0] : null
        }
      };

      // Add certificate document if uploaded
      if (formData.certificateDocument && formData.certificateDocument.fileId) {
        variables.data.certificateDocument = {
          create: [{
            fileId: formData.certificateDocument.fileId,
            public: formData.certificateDocument.public || true,
            filename: formData.certificateDocument.filename || formData.certificateDocument.name || 'Unknown file'
          }]
        };
      }

      let createdRequest;

      if (request) {
        // Update existing request
        await updateRequest({
          variables: {
            filter: { id: { equals: request.id } },
            data: variables.data
          }
        });
        enqueueSnackbar('Certificate request updated successfully', { variant: 'success' });
      } else {
        // Create new request
        const result = await createRequest({ variables });
        createdRequest = result.data.certificateRequestCreate;
        enqueueSnackbar('Certificate request created successfully', { variant: 'success' });
      }

      // Send email using the new workflow system
      if (emailConnected && emailTemplatesData?.emailTemplatesList?.items?.length > 0) {
        try {
          const template = emailTemplatesData.emailTemplatesList.items[0]; // Use first template
          const policyData = selectedPolicy;
          const clientData = selectedClient;
          
          
          const workflowResult = await handleCertificateRequestWorkflow(
            createdRequest,
            template,
            user,
            createEmailLog,
            policyData,
            clientData
          );
          
          if (workflowResult.success) {
            if (workflowResult.emailSent) {
              enqueueSnackbar('Certificate request created and email sent successfully!', { variant: 'success' });
            } else {
              enqueueSnackbar(`Request created but email not sent: ${workflowResult.error}`, { variant: 'warning' });
            }
          } else {
            enqueueSnackbar(`Request created but email failed: ${workflowResult.error}`, { variant: 'warning' });
          }
        } catch (emailError) {
          console.error('Certificate request workflow error:', emailError);
          enqueueSnackbar(`Email sending failed: ${emailError.message}`, { variant: 'warning' });
        }
      } else if (!emailConnected) {
        enqueueSnackbar('Gmail not configured. Email not sent.', { variant: 'warning' });
      } else {
        enqueueSnackbar('No email template found for certificate request.', { variant: 'warning' });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        enqueueSnackbar('Please upload a PDF, JPEG, or PNG file', { variant: 'error' });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        enqueueSnackbar('File size must be less than 10MB', { variant: 'error' });
        return;
      }
      
      setFormData(prev => ({ 
        ...prev, 
        certificateDocument: file,
        certificateReceivedDate: new Date() // Auto-fill when certificate is uploaded
      }));
      enqueueSnackbar('Certificate file uploaded successfully', { variant: 'success' });
    }
  };

  const removeUploadedFile = () => {
    setFormData(prev => ({ 
      ...prev, 
      certificateDocument: null,
      certificateReceivedDate: null // Clear when file is removed
    }));
    enqueueSnackbar('Certificate file removed', { variant: 'info' });
  };

  const customers = customersData?.customersList?.items || [];
  const policies = policiesData?.insurancePoliciesList?.items || [];

  const selectedClient = customers.find(client => client.id === formData.clientId);
  const selectedPolicy = policies.find(policy => policy.id === formData.insurancePolicyId);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select a client and insurance policy to request an Additional Insured Certificate. 
                This will generate an email to the insurance agent requesting the certificate.
                <br /><br />
                <strong>Note:</strong> The email will be sent to the agent email address configured in the selected insurance policy.
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Client</InputLabel>
                <Select
                  value={formData.clientId}
                  onChange={(e) => handleChange('clientId', e.target.value)}
                  disabled={customersLoading}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.address1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Insurance Policy</InputLabel>
                <Select
                  value={formData.insurancePolicyId}
                  onChange={(e) => handleChange('insurancePolicyId', e.target.value)}
                  disabled={policiesLoading}
                >
                  {policies.map((policy) => (
                    <MenuItem key={policy.id} value={policy.id}>
                      {policy.policyNumber} - {policy.provider}
                      {policy.businessUnit && ` (${policy.businessUnit.name})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Request Reason"
                value={formData.requestReason}
                onChange={(e) => handleChange('requestReason', e.target.value)}
                placeholder="Explain why this certificate is needed..."
              />
            </Grid>

            {selectedClient && selectedPolicy && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Request Summary</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Client:</Typography>
                        <Typography>{selectedClient.name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Policy:</Typography>
                        <Typography>{selectedPolicy.policyNumber}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Agent Email:</Typography>
                        <Typography>{selectedPolicy.agentEmail || 'Not specified'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Business Unit:</Typography>
                        <Typography>{selectedPolicy.businessUnit?.name || 'N/A'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Review the email that will be sent to the insurance agent requesting the Additional Insured Certificate. 
                You can edit the content before sending.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Email Preview</Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">To:</Typography>
                    <Typography>{emailData.recipients.join(', ')}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                    <TextField
                      fullWidth
                      value={emailData.subject}
                      onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Body:</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={emailData.body}
                      onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

                         <Grid item xs={12}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 {emailConnected ? (
                   <CheckCircleIcon color="success" />
                 ) : (
                   <WarningIcon color="error" />
                 )}
                 <Typography>
                   Email Connection: {emailConnected ? `${emailType.toUpperCase()} Connected` : 'Not Connected'}
                 </Typography>
               </Box>
             </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Email sent successfully! The certificate request has been created and is now pending response from the insurance agent.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Request Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Client:</Typography>
                      <Typography>{selectedClient?.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Policy:</Typography>
                      <Typography>{selectedPolicy?.policyNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Request Date:</Typography>
                      <Typography>{formData.requestDate ? dayjs(formData.requestDate).format('MMM DD, YYYY') : dayjs().format('MMM DD, YYYY')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Email Sent:</Typography>
                      <Typography>{emailSent ? 'Yes' : 'No'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Email Sent Date:</Typography>
                      <Typography>{formData.emailSentDate ? dayjs(formData.emailSentDate).format('MMM DD, YYYY') : 'Not sent'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Certificate Received Date:</Typography>
                      <Typography>{formData.certificateReceivedDate ? dayjs(formData.certificateReceivedDate).format('MMM DD, YYYY') : 'Not received'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {request && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Request Information:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Request Date: ${dayjs(request.requestDate).format('MMM DD, YYYY')}`} 
                      size="small" 
                    />
                    <Chip 
                      label={`Requested By: ${request.requestedBy?.firstName} ${request.requestedBy?.lastName}`} 
                      size="small" 
                    />
                    {request.emailSentDate && (
                      <Chip 
                        label={`Email Sent: ${dayjs(request.emailSentDate).format('MMM DD, YYYY')}`} 
                        size="small" 
                        color="success"
                      />
                    )}
                    {request.certificateReceivedDate && (
                      <Chip 
                        label={`Certificate Received: ${dayjs(request.certificateReceivedDate).format('MMM DD, YYYY')}`} 
                        size="small" 
                        color="info"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            )}

            {request && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleChange('status', e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Certificate Number"
                    value={formData.certificateNumber}
                    onChange={(e) => handleChange('certificateNumber', e.target.value)}
                    placeholder="Enter certificate number when received"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Certificate Received Date"
                    value={formData.certificateReceivedDate ? dayjs(formData.certificateReceivedDate).format('MM/DD/YYYY') : ''}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Auto-filled when certificate is uploaded"
                    placeholder="Auto-filled when certificate received"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Effective Dates"
                    value={formData.effectiveDates}
                    onClick={() => setDateRangePickerOpen(true)}
                    placeholder="Click to select date range"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <DateRangePicker
                    open={dateRangePickerOpen}
                    onClose={() => setDateRangePickerOpen(false)}
                    onSubmit={handleDateRangeSubmit}
                    onClear={handleDateRangeClear}
                    onChangeStartDate={(date) => handleDateRangeChange(date, formData.effectiveEndDate)}
                    onChangeEndDate={(date) => handleDateRangeChange(formData.effectiveStartDate, date)}
                    startDate={formData.effectiveStartDate}
                    endDate={formData.effectiveEndDate}
                    isError={dateRangeError}
                    title="Select Certificate Effective Dates"
                    variant="calendar"
                  />
                </Grid>
              </>
            )}

            {/* Certificate Upload Section */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Certificate Document Upload
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Upload the certificate document received from the insurance agent. 
                    This will be attached to the request and linked to the client and policy.
                  </Typography>
                  
                  {!formData.certificateDocument ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="certificate-upload"
                        type="file"
                        onChange={handleFileUpload}
                      />
                      <label htmlFor="certificate-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          sx={{ mb: 1 }}
                        >
                          Upload Certificate
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" color="textSecondary">
                        Supported formats: PDF, JPEG, PNG (Max 10MB)
                      </Typography>
                    </Box>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DescriptionIcon color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {formData.certificateDocument.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {(formData.certificateDocument.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          color="error"
                          onClick={removeUploadedFile}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {request ? 'Edit Certificate Request' : 'New Certificate Request'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        
                 {activeStep === 1 && (
           <Button 
             onClick={handleSendEmail}
             variant="contained"
             startIcon={emailLoading ? <CircularProgress size={20} /> : <EmailIcon />}
             disabled={!emailConnected || emailLoading}
           >
             {emailLoading ? 'Sending...' : `Send Email via ${emailType.toUpperCase()}`}
           </Button>
         )}
        
        {activeStep < steps.length - 1 && activeStep !== 1 && (
          <Button 
            onClick={handleNext}
            variant="contained"
            disabled={!formData.clientId || !formData.insurancePolicyId}
          >
            Next
          </Button>
        )}
        
        {activeStep === steps.length - 1 && (
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? (
              <CircularProgress size={20} />
            ) : (
              request ? 'Update Request' : 'Create Request'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CertificateRequestForm;
